import trace from '~/lib/trace';
import {useEffect, useRef, useMemo, useState, useContext} from 'react';
import {View} from 'react-native';
import moment from 'moment';
import {useNavigation, useIsFocused} from '@react-navigation/native';
import {Divider} from 'react-native-paper';
import isNil from 'lodash/isNil';
import {RecyclerListView, DataProvider, LayoutProvider} from 'recyclerlistview';
import PropTypes from 'prop-types';

import sy from '~/styles';
import {useSelector, useDispatch} from '~/lib/hooks';
import {fetchMessages} from '~/actions';
import {user as userApi} from '~/api/private';
import Format from '~/lib/format';
import {
  Pressable,
  Text,
  LessorPersona,
  ServiceRequestStatusIcon,
  UserLabel,
} from '~/components/controls';
import {DealerHeader} from '~/components/shared';
import {STATUS_DESCRIPTIONS} from '~/types/statuses';
import withDimensions from '~/components/hoc/with-dimensions';
import UserContext from '~/components/context/UserContext';
import {
  IsDamageRequest,
  IsReplacementVehicle,
  REQUEST_TYPES,
} from '~/types/request';
import {Tabs} from '~/types/tabs';

import ChatIcon from '~/images/md-icons/chat/materialicons/24px.svg';
import PhotoCameraIcon from '~/images/md-icons/photo_camera/materialicons/24px.svg';
import TextsmsIcon from '~/images/md-icons/textsms/materialicons/24px.svg';
import EmailIcon from '~/images/md-icons/email/materialicons/24px.svg';
import DocsIcon from '~/images/md-icons/docs/materialsymbolsoutlined/24px.svg';
import BuildIcon from '~/images/md-icons/build_repair/materialicons/24px.svg';
import CarCrashIcon from '~/images/md-icons/car_crash/materialicons/24px.svg';
import CarRentalIcon from '~/images/md-icons/car_rental/materialicons/24px.svg';
import UpdateIcon from '~/images/md-icons/update/materialicons/24px.svg';

const ROW_HEIGHT = 96;

const renderDateTimeChange = (message_status, activity_attributes) => {
  if (message_status === 'EXPECTED_COMPLETION_DATE_CHANGED') {
    return (
      <>
        {`Gereed (verwacht): ${moment(
          activity_attributes.expected_completion_date,
        ).format('D MMM')}`}
        &nbsp;(
        <Text
          style={[sy.smallRegular, sy['text-lightgray'], sy['line-through']]}>
          {moment(activity_attributes.previous).format('D MMM')}
        </Text>
        )
      </>
    );
  }

  if (message_status === 'DATE_CHANGED') {
    return (
      <>
        {`Datum afspraak: ${moment(activity_attributes.date).format('D MMM')}`}
        &nbsp;(
        <Text
          style={[sy.smallRegular, sy['text-lightgray'], sy['line-through']]}>
          {moment(activity_attributes.previous).format('D MMM')}
        </Text>
        )
      </>
    );
  }

  return (
    <>
      {`Tijd afspraak: ${activity_attributes.time}`}
      &nbsp;(
      <Text style={[sy.smallRegular, sy['text-lightgray'], sy['line-through']]}>
        {activity_attributes.previous}
      </Text>
      )
    </>
  );
};

const resolveMessageContent = ({
  message_status,
  activity_attributes,
  is_photo,
  dealer_name,
  user,
  attachment_name,
  message,
}) => {
  if (
    [
      'DATE_CHANGED',
      'TIME_CHANGED',
      'EXPECTED_COMPLETION_DATE_CHANGED',
    ].includes(message_status)
  ) {
    return (
      <View style={[sy['flex-row'], sy['items-start'], sy['gap-2']]}>
        <UpdateIcon fill="#828282" width={18} height={18} />
        <Text style={[sy.smallRegular, sy['text-lightgray']]}>
          {renderDateTimeChange(message_status, activity_attributes)}
        </Text>
      </View>
    );
  }

  if (activity_attributes?.type === 'SMS') {
    return (
      <View style={[sy['flex-row'], sy['items-start'], sy['gap-2']]}>
        <TextsmsIcon fill="#4a4a49" width={18} height={18} />
        <Text style={[sy.smallRegular, sy['text-lightgray']]}>
          {message_status === 'SentFailed'
            ? 'SMS verzenden mislukt'
            : 'SMS verzonden'}
        </Text>
      </View>
    );
  }

  if (activity_attributes?.type === 'Email') {
    return (
      <View style={[sy['flex-row'], sy['items-start'], sy['gap-2']]}>
        <EmailIcon fill="#4a4a49" width={18} height={18} />
        <Text style={[sy.smallRegular, sy['text-lightgray']]}>
          E-mail verzonden
        </Text>
      </View>
    );
  }

  if (message_status) {
    return (
      <View style={[sy['flex-row'], sy['items-start'], sy['gap-2']]}>
        <ServiceRequestStatusIcon
          status={message_status}
          iconProps={{width: 18, height: 18}}
        />
        <Text style={[sy.smallRegular, sy['text-lightgray']]}>
          {STATUS_DESCRIPTIONS[message_status]}
        </Text>
      </View>
    );
  }

  if (!isNil(is_photo)) {
    return (
      <>
        {dealer_name && (
          <Text
            style={[
              sy.smallRegular,
              sy['text-lightgray'],
              sy['whitespace-nowrap'],
            ]}>
            {user?.first_name ?? 'Jij'}:&nbsp;
          </Text>
        )}
        {is_photo ? (
          <>
            <PhotoCameraIcon width={18} height={18} fill="#828282" />
            <Text style={[sy.smallRegular, sy['text-lightgray']]}>
              &nbsp;Foto
            </Text>
          </>
        ) : (
          <>
            <View style={sy['shrink-0']}>
              <DocsIcon width={18} height={18} fill="#828282" />
            </View>
            <Text style={[sy.smallRegular, sy.truncate, sy['text-lightgray']]}>
              &nbsp;{attachment_name}
            </Text>
          </>
        )}
      </>
    );
  }

  if (message) {
    return (
      <Text
        style={[
          sy.smallRegular,
          sy['text-lightgray'],
          {
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
          },
        ]}>
        {dealer_name ? <>{user?.first_name ?? 'Jij'}:&nbsp;</> : <></>}
        {message}
      </Text>
    );
  }

  return null;
};

const Message = ({
  style,
  service_request_id,
  parent_id,
  request_type,
  user_id,
  dealer_name,
  lessor,
  license,
  assigned_user_id,
  activity_attributes,
  message,
  is_photo,
  attachment_name,
  message_timestamp,
  message_status,
  unread,
}) => {
  const navigation = useNavigation();

  const {me} = useContext(UserContext);

  const [user, setUser] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      const user = await userApi.find(user_id);
      setUser(user);
    };

    if (user_id && me && me?.id !== user_id) {
      fetch().catch(trace);
    }
  }, [me, user_id]);

  const timestamp = moment(message_timestamp);
  let duration = timestamp.format('DD-MM-YYYY');
  if (timestamp.isSame(moment(), 'day')) {
    duration = timestamp.format('H:mm');
  }
  const onPress = () => {
    if (IsDamageRequest(request_type)) {
      navigation.navigate('Damage', {
        damage_id: service_request_id,
        tab: Tabs.Messages,
      });
    } else if (IsReplacementVehicle(request_type)) {
      navigation.navigate('ReplacementVehicle', {
        request_id: parent_id,
        tab: Tabs.Messages,
      });
    } else {
      navigation.navigate('Request', {
        request_id: service_request_id,
        tab: Tabs.Messages,
      });
    }
  };

  const content = resolveMessageContent({
    message_status,
    activity_attributes,
    is_photo,
    dealer_name,
    user,
    attachment_name,
    message,
  });

  let requestIcon = <BuildIcon fill="#4a4a49" width={18} height={18} />;
  if ([REQUEST_TYPES.ROB_DAMAGE_REQUEST].includes(request_type)) {
    requestIcon = <CarCrashIcon fill="#4a4a49" width={18} height={18} />;
  } else if (
    [
      REQUEST_TYPES.ROB_REPLACEMENT_VEHICLE,
      REQUEST_TYPES.FLEET_REPLACEMENT_VEHICLE,
    ].includes(request_type)
  ) {
    requestIcon = <CarRentalIcon fill="#4a4a49" width={18} height={18} />;
  }

  return (
    <Pressable style={[style]} onPress={onPress}>
      <View
        style={[
          sy['flex-1'],
          sy['flex-row'],
          sy['px-4'],
          sy['py-3'],
          sy['gap-4'],
        ]}>
        <LessorPersona key={lessor} lessor={lessor} />
        <View style={[sy['flex-1']]}>
          <View style={[sy['flex-row'], sy['items-between'], sy['gap-2']]}>
            <View style={[sy['flex-1']]}>
              <Text style={[sy.medium, sy.truncate, {lineHeight: 24}]}>
                {Format.license(license)}&nbsp;&middot;&nbsp;
                {lessor}
              </Text>
            </View>
            <Text
              style={[
                sy.smallRegular,
                {lineHeight: 24},
                unread > 0 && {
                  color: '#231fda',
                },
              ]}>
              {duration}
            </Text>
          </View>
          <View style={[sy['flex-row'], sy['gap-2'], sy['flex-1']]}>
            <View style={[sy['flex-1'], sy['flex-row']]}>{content}</View>
            <View style={[sy['flex-row'], sy['gap-2']]}>
              {requestIcon}
              {unread > 0 && (
                <View
                  style={[
                    sy['items-center'],
                    sy['justify-center'],
                    {
                      backgroundColor: '#231fda',
                      borderRadius: 90,
                      width: 16,
                      height: 16,
                    },
                  ]}>
                  <Text
                    style={[
                      sy.mediumBold,
                      {lineHeight: 10, fontSize: 10, color: '#ffffff'},
                    ]}>
                    {unread}
                  </Text>
                </View>
              )}
            </View>
          </View>
        </View>
        <UserLabel key={service_request_id} user_id={assigned_user_id} />
      </View>
    </Pressable>
  );
};
Message.propTypes = {
  style: PropTypes.any,
  service_request_id: PropTypes.any,
  parent_id: PropTypes.any,
  request_type: PropTypes.any,
  user_id: PropTypes.any,
  dealer_name: PropTypes.any,
  lessor: PropTypes.any,
  license: PropTypes.any,
  assigned_user_id: PropTypes.any,
  activity_attributes: PropTypes.any,
  message: PropTypes.any,
  is_photo: PropTypes.any,
  attachment_name: PropTypes.any,
  message_timestamp: PropTypes.any,
  message_status: PropTypes.any,
  unread: PropTypes.any,
};

const Overview = ({dimensions}) => {
  const isFocused = useIsFocused();
  const dispatch = useDispatch();

  const {window} = dimensions;
  const width = Math.min(window.width, 412);

  const listViewRef = useRef(null);
  const dataProvider = useRef(
    new DataProvider(
      (r1, r2) => r1?.service_request_id !== r2?.service_request_id,
    ),
  );

  const [counter, setCounter] = useState(0);
  const messages = useSelector((state) => state.messages);

  useEffect(() => {
    if (isFocused) {
      dispatch(fetchMessages());
    }
  }, [isFocused]);

  useEffect(() => {
    dataProvider.current = dataProvider.current.cloneWithRows(messages);
    listViewRef.current?.forceRerender();
    setCounter(counter + 1);
  }, [messages]);

  const layoutProvider = useMemo(() => {
    const provider = new LayoutProvider(
      (index) => index,
      (index, dim) => {
        dim.width = width;
        dim.height = ROW_HEIGHT;
      },
    );

    provider.shouldRefreshWithAnchoring = false;
    return provider;
  }, [width]);

  const onRowRenderer = (type, data) => {
    return (
      <View style={[sy['mx-2']]}>
        <Message
          {...data}
          style={[
            sy['bg-white'],
            sy['rounded-lg'],
            {minHeight: 88, overflow: 'hidden'},
          ]}
        />
      </View>
    );
  };

  return (
    <View style={[sy.mainView, sy['bg-gray95']]}>
      <View style={[sy['px-4'], sy['pt-4'], sy['pb-3'], sy['bg-white']]}>
        <DealerHeader
          titleStyle={{textTransform: 'capitalize'}}
          title="Berichten"
          icon={<ChatIcon fill="#ffffff" height={18} width={18} />}
        />
      </View>
      <Divider style={sy['bg-gray88']} />
      <RecyclerListView
        ref={listViewRef}
        scrollViewProps={{
          contentContainerStyle: [sy['bg-gray-95'], sy['mt-2']],
        }}
        dataProvider={dataProvider.current}
        layoutProvider={layoutProvider}
        rowRenderer={onRowRenderer}
        initialRenderIndex={0}
      />
    </View>
  );
};
Overview.propTypes = {
  dimensions: PropTypes.any,
};

export default withDimensions(Overview);
